import { useState, useEffect } from "react";

function Collapsible({ title, children, bg = "bg-white" }: { title: string; children: React.ReactNode; bg?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${bg} rounded-xl border border-slate-200 mb-6`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <h2 className="text-base font-semibold text-slate-800">{title}</h2>
        <svg
          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const [activeFudges, setActiveFudges] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/fudges")
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setActiveFudges(data.length));
  }, []);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Welcome to Fudge Log — here's how to get started.</p>
      </div>

      {/* Active fudges count */}
      <div className="mb-8">
        <a href="/fudges" className="block bg-white rounded-xl border border-slate-200 p-6 hover:border-blue-300 transition-colors">
          <p className="text-sm font-medium text-slate-500 mb-1">Active Fudges</p>
          <p className={`text-5xl font-bold ${activeFudges === null ? "text-slate-300" : activeFudges > 0 ? "text-amber-500" : "text-green-500"}`}>
            {activeFudges === null ? "…" : activeFudges}
          </p>
          <p className="text-xs text-slate-400 mt-2">
            {activeFudges === null ? "" : activeFudges === 0 ? "All clear — no changes currently applied." : `${activeFudges === 1 ? "1 change" : `${activeFudges} changes`} currently applied on plant. Tap to view.`}
          </p>
        </a>
      </div>

      <Collapsible title="How to register a fudge">
        <ol className="space-y-5">
          <Step number={1} title="Get a fresh QR tag">
            Use an unused QR tag. Each tag has a unique QR code
            printed on it. Do not reuse a tag that has already been attached to a previous fudge.
          </Step>
          <Step number={2} title="Make your change and attach the tag">
            Make the physical change on plant (remove the wire, isolate the relay, force the output,
            etc.). Stick the QR tag at the exact point of the change so it is visible to anyone
            working in that area.
          </Step>
          <Step number={3} title="Scan the QR code with your phone">
            Connect your phone to the site WiFi, then open your camera app and scan the QR tag.
            Your browser will open automatically — no app required.
          </Step>
          <Step number={4} title="Fill in the fudge details">
            Complete the short form that appears:
            <ul className="mt-2 ml-4 space-y-1 text-sm text-slate-500 list-disc">
              <li><span className="font-medium text-slate-700">Description</span> — why is this fudge needed?</li>
              <li><span className="font-medium text-slate-700">Change Detail</span> — what was physically done?</li>
              <li><span className="font-medium text-slate-700">Location</span> — panel reference or area (optional)</li>
              <li><span className="font-medium text-slate-700">Your Name</span> — pre-filled if you are logged in</li>
            </ul>
          </Step>
          <Step number={5} title="Tap Register Fudge">
            Submit the form. The change is now recorded and visible to everyone on the network.
            The tag on the plant always means there is an active, unresolved change.
          </Step>
        </ol>
      </Collapsible>

      <Collapsible title="How to reinstate (close out) a fudge">
        <ol className="space-y-5">
          <Step number={1} title="Reverse the physical change">
            Put the wire back, re-engage the relay, remove the force, etc. Make sure the change has
            been physically reversed before closing out the fudge.
          </Step>
          <Step number={2} title="Scan the QR tag still attached at the point of change">
            Scan the same tag that was placed when the fudge was registered. The app will show the
            full fudge details so you can confirm you are reinstating the correct change.
          </Step>
          <Step number={3} title="Tap Reinstate Fudge and confirm">
            Enter your name (pre-filled if logged in), tap <strong>Reinstate Fudge</strong>, then
            confirm. The fudge is marked as reinstated and the tag is spent.
          </Step>
          <Step number={4} title="Remove and discard the tag">
            Peel the QR tag off the panel and discard it. A spent tag on the plant means the fudge
            is closed — removing it keeps the physical and digital records in sync.
          </Step>
        </ol>
      </Collapsible>

      <Collapsible title="Quick tips" bg="bg-slate-50">
        <ul className="space-y-2 text-sm text-slate-600">
          <Tip>A tag on the plant <strong>always</strong> means an active, unresolved change. If you see a tag and it is not in the app, register it immediately.</Tip>
          <Tip>Running low on tags? An admin can print a new batch from <strong>Tag Management</strong>.</Tip>
          <Tip>Need to repeat a past fudge? Browse <strong>History</strong>, find the relevant record, and tap <strong>Re-apply</strong> to pre-fill the form.</Tip>
          <Tip>If a tag is lost or damaged, an admin can reinstate the fudge manually from the <strong>Active Fudges</strong> page without scanning.</Tip>
        </ul>
      </Collapsible>
    </div>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
        {number}
      </span>
      <div>
        <p className="text-sm font-semibold text-slate-800 mb-0.5">{title}</p>
        <p className="text-sm text-slate-500">{children}</p>
      </div>
    </li>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="text-blue-500 flex-shrink-0">›</span>
      <span>{children}</span>
    </li>
  );
}
